package com.siriusxm.pia.views.unifiedaggregator.events

import com.siriusxm.pia.components.TimeRange
import kotlin.time.Clock
import kotlin.time.Duration.Companion.days
import kotlin.time.Instant

/**
 * How long the aggregator keeps the CloudWatch log groups every events search runs against
 * (`<bus>-events` and `<base>-outgoing-events`). Mirrors `RetentionDays.THREE_MONTHS` in the
 * aggregator's `infrastructure/lib/EventBus.ts`; keep the two in step.
 *
 * This is an approximation of what the backend can serve, not an exact bound: a log group younger
 * than its retention period can only be searched back to its creation time. The aggregator is the
 * authority, and validates every search itself (UCA-1168).
 */
val EVENT_RETENTION = 90.days

/** Shown instead of searching when the selected range falls entirely outside [EVENT_RETENTION]. */
const val EVENT_RETENTION_MESSAGE =
    "Events are only kept for 90 days. Choose a time period within the last 90 days."

/**
 * The note shown in the events time range pickers' "Custom" popover, explaining why older end dates
 * are greyed out and why a range that starts earlier only returns the retained part of it.
 */
const val EVENT_RETENTION_NOTE = "Events are only kept for 90 days."

/** The earliest instant an events search can return anything for, as of [now]. */
fun eventRetentionCutoff(now: Instant = Clock.System.now()): Instant = now - EVENT_RETENTION

/**
 * Whether this range ends before the retention cutoff, so no event in it can still be searched.
 *
 * CloudWatch rejects such a query outright, which the aggregator currently surfaces as a 500
 * (UCA-1168). A range that only *starts* before the cutoff is still searchable — CloudWatch
 * returns whatever falls inside retention — so it is deliberately not treated as outside.
 */
fun TimeRange.isOutsideEventRetention(now: Instant = Clock.System.now()): Boolean =
    range(relativeTo = now).endInclusive < eventRetentionCutoff(now)
